import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookPlus, Copy, Loader2, Sparkles, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { dammInfo, NODE_TYPES } from "../lib/constants";

const STATUS_TXT = { nar_fram: "Når fram", nar_delvis_fram: "Når delvis fram", nar_inte_fram: "Når inte fram", forandras: "Förändras" };
const STATUS_CLR = { nar_fram: "#34D399", nar_delvis_fram: "#F59E0B", nar_inte_fram: "#EF4444", forandras: "#38BDF8" };
const AUDS = [
    { id: "ledning", label: "Ledning" },
    { id: "chef", label: "Chef" },
    { id: "medarbetare", label: "Medarbetare" },
];

function plainMarkdown(text) {
    return (text || "")
        .replace(/^#{1,6}\s*/gm, "")
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/__([^_]+)__/g, "$1")
        .replace(/`([^`]+)`/g, "$1");
}

export default function EntityPortal({ nodeId, audience = "ledning", focusRoleId = null, inline = false, onAddToStory, onClose }) {
    const [portal, setPortal] = useState(null);
    const [explainAud, setExplainAud] = useState(AUDS.some((a) => a.id === audience) ? audience : "medarbetare");
    const [explaining, setExplaining] = useState(false);
    const [explanation, setExplanation] = useState("");

    useEffect(() => {
        setPortal(null);
        setExplanation("");
        api.get(`/nodes/${nodeId}/portal`)
            .then((r) => setPortal(r.data))
            .catch((e) => toast.error(formatApiError(e, "Kunde inte ladda portalen")));
    }, [nodeId]);

    if (!portal) {
        return (
            <div className="flex-1 flex items-center justify-center p-8" data-testid="entity-portal-loading">
                <Loader2 className="w-5 h-5 animate-spin text-current" />
            </div>
        );
    }

    const n = portal.node;
    const L = portal.labels || {};
    const focusedRole = (n.roller || []).find((r) => r.id === focusRoleId);
    const visibleRoles = focusedRole ? [focusedRole] : (n.roller || []);

    const explain = async () => {
        setExplaining(true);
        try {
            const r = await api.post(`/nodes/${nodeId}/explain`, { mottagare: explainAud, roll_id: focusRoleId || undefined });
            setExplanation(r.data.text);
        } catch (e) {
            toast.error(formatApiError(e, "AI-förklaringen misslyckades"));
        } finally {
            setExplaining(false);
        }
    };

    const copyText = async () => {
        try {
            await navigator.clipboard.writeText(explanation);
            toast.success("AI-texten kopierad – klistra in den i din rapport");
        } catch {
            toast.error("Kunde inte kopiera – markera texten och kopiera manuellt");
        }
    };

    return (
        <div className={`flex-1 overflow-y-auto panel-scroll p-4 xl:p-5 space-y-5 ${inline ? "" : ""}`} data-testid="entity-portal-body">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">
                        {focusedRole ? `Roll · ${n.label}` : `${NODE_TYPES[n.type]?.label || n.type} · Kontext-portal`}
                    </p>
                    <h2 className="font-display font-bold text-xl text-slate-100 leading-snug mt-0.5" data-testid="portal-title">{focusedRole?.namn || n.label}</h2>
                </div>
                {!inline && onClose && (
                    <button data-testid="portal-close" onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors shrink-0">
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {(n.antal_anstallda != null || n.kostnad != null || n.reviderad || focusedRole?.kostnad != null || focusedRole?.reviderad) && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="portal-stats">
                    {n.antal_anstallda != null && !focusedRole && (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-300 bg-deepsea/60 border border-border rounded-full px-2.5 py-1">{n.antal_anstallda} anställda</span>
                    )}
                    {(focusedRole?.kostnad ?? n.kostnad) != null && (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2.5 py-1">≈ {focusedRole?.kostnad ?? n.kostnad} tkr/år</span>
                    )}
                    {(focusedRole?.reviderad || n.reviderad) && (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 bg-deepsea/60 border border-border rounded-full px-2.5 py-1">Reviderad {focusedRole?.reviderad || n.reviderad}</span>
                    )}
                </div>
            )}

            {n.description && <p className="text-sm text-slate-300 leading-relaxed">{n.description}</p>}
            {n.varde && (
                <div className="rounded-xl border border-buoy/30 bg-buoy/5 p-3">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-buoy mb-1">Värde kontra kostnad</p>
                    <p className="text-xs text-slate-300 leading-relaxed">{n.varde}</p>
                </div>
            )}

            {visibleRoles.length > 0 && (
                <div data-testid="portal-roller">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">{focusedRole ? "Vald roll" : "Roller i avdelningen"}</p>
                    <div className="space-y-2">
                        {visibleRoles.map((r) => (
                            <div key={r.id} className="rounded-lg border border-border bg-abyss/40 p-2.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-semibold text-slate-200">{r.namn}</span>
                                    {r.kostnad != null && <span className="font-mono text-[9px] text-amber-300">≈{r.kostnad} tkr/år</span>}
                                    {r.reviderad && <span className="font-mono text-[9px] text-slate-500">Rev. {r.reviderad}</span>}
                                </div>
                                {r.beskrivning && <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{r.beskrivning}</p>}
                                {(r.rapporterar_till || []).length > 0 && (
                                    <p className="text-[11px] text-slate-400 mt-1">
                                        Rapporterar till: <span className="text-slate-200">{(r.rapporterar_till || []).map((x) => (portal.labels || {})[x] || "?").join(", ")}</span>
                                    </p>
                                )}
                                {(r.aven_i || []).length > 0 && (
                                    <p className="text-[11px] text-slate-400 mt-0.5">
                                        Arbetar även i: <span className="text-tide">{(r.aven_i || []).map((x) => (portal.labels || {})[x] || "?").join(", ")}</span>
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {(n.uppgifter || []).length > 0 && (
                <div data-testid="portal-uppgifter">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Uppgifter</p>
                    <div className="space-y-1">
                        {n.uppgifter.map((u) => (
                            <p key={u.id} className="text-[11px] text-slate-300">· {u.namn}{u.beskrivning ? ` – ${u.beskrivning}` : ""}</p>
                        ))}
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-aidraft/30 bg-aidraft/5 p-3.5" data-testid="portal-explain">
                <p className="font-mono text-[9px] uppercase tracking-widest text-aidraft mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> AI-förklaring per mottagare
                </p>
                <div className="flex items-center gap-1 mb-2.5">
                    {AUDS.map((a) => (
                        <button key={a.id} data-testid={`portal-aud-${a.id}`} onClick={() => setExplainAud(a.id)}
                            className={`rounded-md px-2 py-1 text-[10px] font-medium transition-colors ${explainAud === a.id ? "bg-aidraft/25 text-aidraft" : "text-slate-500 hover:text-slate-300"}`}>
                            {a.label}
                        </button>
                    ))}
                </div>
                <button data-testid="portal-explain-button" onClick={explain} disabled={explaining}
                    className="w-full flex items-center justify-center gap-2 rounded-lg bg-aidraft/15 border border-aidraft/40 text-aidraft py-2 text-xs font-semibold hover:bg-aidraft/25 transition-colors disabled:opacity-50">
                    {explaining ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    Förklara för {AUDS.find((a) => a.id === explainAud)?.label.toLowerCase()}
                </button>
                {explanation && (
                    <div className="mt-3">
                        <p data-testid="portal-explain-text" className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{plainMarkdown(explanation)}</p>
                        <button data-testid="portal-copy-explain" onClick={copyText}
                            className="mt-2 flex items-center gap-1.5 text-[11px] text-current hover:text-tide transition-colors">
                            <Copy className="w-3 h-3" /> Kopiera texten till rapporten
                        </button>
                    </div>
                )}
            </div>

            {portal.flows.length > 0 && (
                <div data-testid="portal-flows">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Flöden genom {n.label}</p>
                    <div className="space-y-1.5">
                        {portal.flows.map((f) => (
                            <div key={f.id} data-testid={`portal-flow-${f.id}`} className="flex items-center gap-2 text-xs rounded-lg border border-border bg-abyss/40 px-2.5 py-1.5">
                                <span className="text-slate-300 flex-1 min-w-0 truncate">{L[f.source] || "?"} → {L[f.target] || "?"}</span>
                                <span className="font-mono text-[9px] uppercase tracking-widest shrink-0" style={{ color: STATUS_CLR[f.flodesstatus || "nar_fram"] }}>
                                    {STATUS_TXT[f.flodesstatus || "nar_fram"]}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {portal.relations.length > 0 && (
                <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Relationer</p>
                    <div className="space-y-1">
                        {portal.relations.map((r) => (
                            <p key={r.id} className="text-[11px] text-slate-400">
                                {L[r.source] || "?"} <span className="text-current">–{r.label || r.perspective}→</span> {L[r.target] || "?"}
                            </p>
                        ))}
                    </div>
                </div>
            )}

            {portal.dammar.length > 0 && (
                <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Dammar kopplade hit</p>
                    <div className="space-y-2">
                        {portal.dammar.map((b) => {
                            const info = dammInfo(b);
                            return (
                                <div key={b.id} className="rounded-lg border p-2.5" style={{ borderColor: `${info.color}44`, background: `${info.color}0a` }}>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-semibold" style={{ color: info.color }}>{info.label}</span>
                                        <span className="font-mono text-[9px] text-slate-500">{b.allvarlighet}/5 · {b.status === "bekraftad" ? "bekräftad" : "förslag"}</span>
                                    </div>
                                    {b.notering && <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{b.notering}</p>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {portal.sources.length > 0 && (
                <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Empirisk förankring</p>
                    <div className="space-y-2">
                        {portal.sources.map((s) => (
                            <div key={s.id} className="rounded-lg border border-border bg-abyss/40 p-2.5">
                                <p className="text-xs font-semibold text-slate-200">{s.titel}</p>
                                {s.utdrag && <p className="text-[11px] text-slate-400 italic mt-1 leading-relaxed">"{s.utdrag}"</p>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {portal.buoys.filter((b) => b.insights?.tematisk_insikt).length > 0 && (
                <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Boj-insikter</p>
                    {portal.buoys.filter((b) => b.insights?.tematisk_insikt).map((b) => (
                        <p key={b.id} className="text-[11px] text-slate-400 leading-relaxed mb-1.5">
                            <span className="text-slate-200 font-medium">{b.etikett || "Boj"}:</span> {b.insights.tematisk_insikt}
                        </p>
                    ))}
                </div>
            )}

            {onAddToStory && (
                <button data-testid="portal-add-story" onClick={() => onAddToStory({ kind: "entitet", node_id: nodeId, notering: "" })}
                    className="w-full flex items-center justify-center gap-2 rounded-lg border border-current/40 bg-current/10 text-current py-2 text-xs font-semibold hover:bg-current/20 transition-colors">
                    <BookPlus className="w-3.5 h-3.5" /> Lägg till som steg i berättelse
                </button>
            )}
        </div>
    );
}
